import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { ChartArea, EllipsisVertical, Info } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/catalyst/Badge";
import { Button } from "@/components/catalyst/Button";
import {
	Dropdown,
	DropdownButton,
	DropdownItem,
	DropdownMenu,
} from "@/components/catalyst/Dropdown";
import { TableBody, TableCell, TableRow } from "@/components/catalyst/Table";
import { InlineEditor } from "@/components/InlineEditor";
import { Time } from "@/components/Time";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/Tooltip";
import { orpc } from "@/orpc/client";

export function Partners() {
	const queryClient = useQueryClient();

	const { data: partners } = useSuspenseQuery(
		orpc.partners.list.queryOptions({ input: {} }),
	);

	const toggleActiveMutation = useMutation(
		orpc.partners.update.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: orpc.partners.list.key() });
				toast.success("Partner updated successfully");
			},
			onError: (error) => {
				toast.error(error.message);
			},
		}),
	);

	const deleteMutation = useMutation(
		orpc.partners.remove.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: orpc.partners.list.key() });
				toast.success("Partner deleted successfully");
			},
			onError: (error) => {
				toast.error(error.message);
			},
		}),
	);

	const stripeConnectMutation = useMutation(
		orpc.stripePartnerConnection.create.mutationOptions({
			onSuccess: (data) => {
				if (data.emailSent) {
					toast.success(
						`Stripe Connect invitation sent to ${data.partnerEmail.toLowerCase()}`,
					);
				}
				queryClient.invalidateQueries({ queryKey: orpc.partners.list.key() });
			},
			onError: (error) => {
				toast.error(`Failed to connect: ${error.message}`);
			},
		}),
	);

	const handleStripeConnect = (
		partnerId: string,
		appId: string | null | undefined,
	) => {
		if (!appId) {
			toast.error("Partner must be linked to an app first");
			return;
		}
		stripeConnectMutation.mutate({
			partnerId,
			refreshUrl: `${window.location.origin}/partners`,
			returnUrl: `${window.location.origin}/partners?success=true`,
		});
	};

	return (
		<TableBody>
			{partners.map((partner) => (
				<TableRow key={partner.id}>
					<TableCell className="font-medium text-base-content">
						<InlineEditor<typeof partner>
							field="name"
							id={partner.id}
							currentValue={partner.name}
							procedure={orpc.partners.update}
							invalidateKeys={[orpc.partners.list.key()]}
						/>
					</TableCell>
					<TableCell>{partner.email.toLocaleLowerCase()}</TableCell>
					<TableCell>
						{partner.active ? (
							<Badge color="green">Active</Badge>
						) : (
							<Badge color="red">Inactive</Badge>
						)}
					</TableCell>
					<TableCell>
						<div className="flex items-center gap-1.5">
							<Badge
								color={getStripeStatusColor(partner.stripeOnboardingStatus)}
							>
								{getStripeStatusLabel(partner.stripeOnboardingStatus)}
							</Badge>
							{getStripeStatusInfo(partner.stripeOnboardingStatus) && (
								<Tooltip>
									<TooltipTrigger className="text-base-content/50 hover:text-base-content transition-colors">
										<Info className="size-4" />
									</TooltipTrigger>
									<TooltipContent className="max-w-xs">
										{getStripeStatusInfo(partner.stripeOnboardingStatus)}
									</TooltipContent>
								</Tooltip>
							)}
						</div>
					</TableCell>
					<TableCell>
						{partner.appId ? (
							<Badge color="zinc">Linked</Badge>
						) : (
							<Badge color="orange">Unlinked</Badge>
						)}
					</TableCell>
					<TableCell>
						<Time date={partner.createdAt} />
					</TableCell>
					<TableCell>
						<div className="flex items-center justify-end gap-3">
							<Button
								plain
								className="cursor-pointer"
								aria-label="View analytics"
								href={`/partners/${partner.id}`}
							>
								<ChartArea className="size-5" />
							</Button>
							<Dropdown>
								<DropdownButton
									plain
									aria-label="More options"
									className="cursor-pointer"
								>
									<EllipsisVertical className="size-5" />
								</DropdownButton>
								<DropdownMenu anchor="bottom end">
									{partner.stripeOnboardingStatus === "complete" ? (
										<DropdownItem
											onClick={() =>
												handleStripeConnect(partner.id, partner.appId)
											}
										>
											Manage Stripe
										</DropdownItem>
									) : (
										<DropdownItem
											onClick={() =>
												handleStripeConnect(partner.id, partner.appId)
											}
											disabled={!partner.appId}
										>
											Connect to Stripe
										</DropdownItem>
									)}
									<DropdownItem
										onClick={() =>
											toggleActiveMutation.mutate({
												id: partner.id,
												active: !partner.active,
												appId: partner.appId,
											})
										}
										disabled={!partner.appId}
									>
										{partner.active ? "Disable" : "Enable"}
									</DropdownItem>
									<DropdownItem
										onClick={() => {
											if (
												confirm("Are you sure you want to delete this partner?")
											) {
												deleteMutation.mutate({ id: partner.id });
											}
										}}
									>
										Delete
									</DropdownItem>
								</DropdownMenu>
							</Dropdown>
						</div>
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);
}

// Helper functions for Stripe onboarding status display
function getStripeStatusColor(
	status: string | null | undefined,
): "emerald" | "amber" | "blue" | "orange" | "zinc" {
	switch (status) {
		case "complete":
			return "emerald";
		case "pending":
			return "amber";
		case "creating":
			return "blue";
		case "restricted":
			return "orange";
		default:
			return "zinc";
	}
}

function getStripeStatusLabel(status: string | null | undefined): string {
	switch (status) {
		case "complete":
			return "Connected";
		case "pending":
			return "Pending Setup";
		case "creating":
			return "Creating...";
		case "restricted":
			return "Restricted";
		default:
			return "Not Connected";
	}
}

function getStripeStatusInfo(status: string | null | undefined): string | null {
	switch (status) {
		case "pending":
			return "Partner needs to complete Stripe onboarding to receive payouts.";
		case "creating":
			return "Stripe account is being created...";
		case "restricted":
			return "Stripe has temporarily restricted this account. Partner may need to provide additional information.";
		default:
			return null;
	}
}
